import type {
  ApiEndpointQuery,
  EndpointDefinitions,
  QueryActionCreatorResult,
  QueryArgFrom,
  QueryDefinition,
  StartQueryActionCreatorOptions,
} from '@reduxjs/toolkit/query'
import {defaultSerializeQueryArgs} from '@reduxjs/toolkit/query'
import {useLoaderData} from 'react-router'

import type {AppThunk} from '../actions/types'
import {truthy} from '../utils/guards'
import {keyBy} from '../utils/keyBy'

export type AnyQueryDefinition = QueryDefinition<any, any, any, any, any>
export type Endpoint<D extends AnyQueryDefinition> = ApiEndpointQuery<D, EndpointDefinitions> & {
  useQuerySubscription(arg: QueryArgFrom<D>): unknown
}

export type LoadedEndpoint<D extends AnyQueryDefinition = AnyQueryDefinition> = {
  endpoint: Endpoint<D>
  arg: QueryArgFrom<D>
  result: QueryActionCreatorResult<D>
}
export const loadEndpoint =
  <D extends AnyQueryDefinition>(
    endpoint: Endpoint<D>,
    arg: QueryArgFrom<D>,
    options?: StartQueryActionCreatorOptions,
  ): AppThunk<LoadedEndpoint<D>> =>
  dispatch => ({
    endpoint,
    arg,
    result: dispatch(endpoint.initiate(arg, {subscribe: false, ...options})),
  })

type PreloadedEndpointSubscriberProps<D extends AnyQueryDefinition> = Pick<
  LoadedEndpoint<D>,
  'endpoint' | 'arg'
>

function PreloadedEndpointSubscriber<D extends AnyQueryDefinition>({
  endpoint,
  arg,
}: PreloadedEndpointSubscriberProps<D>) {
  endpoint.useQuerySubscription(arg)
  return null
}

type PreloadedEndpointsSubscriberProps = {
  children: React.ReactNode
}

export type LoadedEndpoints = Array<LoadedEndpoint | null | undefined | false | 0 | ''>

export type PreloadedEndpointsLoaderData = {
  preloadedEndpoints?: LoadedEndpoints
}

export function PreloadedEndpointsSubscriber({children}: PreloadedEndpointsSubscriberProps) {
  const {preloadedEndpoints = []} = useLoaderData() as PreloadedEndpointsLoaderData
  const endpointsByCacheKey = keyBy(preloadedEndpoints.filter(truthy), item =>
    defaultSerializeQueryArgs({
      queryArgs: item.arg,
      endpointName: item.endpoint.name,
      endpointDefinition: {} as AnyQueryDefinition, // not used in defaultSerializeQueryArgs
    }),
  )
  return (
    <>
      {Object.entries(endpointsByCacheKey).map(([key, item]) => (
        <PreloadedEndpointSubscriber key={key} {...item} />
      ))}
      {children}
    </>
  )
}
